import {indexationStore} from '@store/settings/indexation/indexation.store';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {Indexation, ManageIndexation, ManageIndexationRate} from '@store/settings/indexation/indexation.model';
import APIAxios, {APIRoutes} from '@api/axios.api';
import {AxiosError, AxiosResponse} from 'axios';
import SnackError from '@utils/error.utils';
import {getIndexationDataSource} from '@store/settings/indexation/indexation.requests';
import {setEntities, updateEntities} from '@ngneat/elf-entities';
import {SelectItem} from '@components/inputs/select/Select.component';
import {ClientsPagination} from '@store/clients/clients.model';
import {SuppliersPagination} from '@store/suppliers/suppliers.model';
import {OrganisationStatus} from '@store/common/organisation.enums';
import {TableConfig} from '@constants/table.constants';

export class IndexationService {
  store = indexationStore;

  resetStore = () => this.store.reset();

  getIndexations = (): Observable<Indexation[]> => {
    return from(APIAxios({
      ...APIRoutes.GETIndexations(),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Indexation[]>) => {
          return response.data;
        }),
        tap((indexations) => {
          this.store.update(
            setEntities(indexations),
            getIndexationDataSource.setSuccess(),
          );
        }),
        getIndexationDataSource.trackRequestStatus(),
      );
  };

  getIndexation(indexationId: string) {
    return from(APIAxios({
      ...APIRoutes.GETIndexation(indexationId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Indexation>) => {
          return response.data;
        }),
      );
  }

  updateRate(indexationId: number | string, rate: ManageIndexationRate) {
    return from(APIAxios({
      ...APIRoutes.POSTIndexationRate(indexationId),
      data: rate,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Indexation>) => {
          return response.data;
        }),
      );
  }

  saveIndexation(indexationId: number | string, indexation: ManageIndexation) {
    return from(APIAxios({
      ...APIRoutes.PATCHIndexation(indexationId),
      data: indexation,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap(() => {
          this.store.update(
            updateEntities([indexationId as number], indexation),
          );
        })
      );
  }

  createIndexation(indexation: ManageIndexation) {
    return from(APIAxios({
      ...APIRoutes.POSTIndexation(),
      data: indexation,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getIndexations())
      );
  }
}

export const indexationService = new IndexationService();
